import { createReadingConditionStore } from '@/stores/conditionStore';
import { QuestionnaireConditionChecker } from '@/utils/conditions/conditionChecker/QuestionnaireConditionChecker';
import questionsHandler from "@/utils/questionsHandler";

export class ConditionsCalculator {
  constructor(conditionsDTO) {
    this.conditionsDTO = conditionsDTO
    this.questionnaireConditionChecker = new QuestionnaireConditionChecker()
  }

  calculateQuestionConditionResult(
    conditionType,
    conditionValue,
    question,
    scopedContext,
    attachmentType = 'none',
    attachmentId = null,
    parentQuestionId = false
  ) {
    const parentQuestionBuiltId = questionsHandler.buildId(parentQuestionId, attachmentType, attachmentId)
    if (parentQuestionId && this.conditionsDTO.invisibleQuestions.has(parentQuestionBuiltId)) {
      return false
    }
    let conditionResult
    if ( this.conditionsDTO.questionBelongsToInvisibleGroup(question.group?.id, attachmentType, attachmentId)) {
      conditionResult = false
    } else if (conditionType === 'mandatory' && (conditionValue.length === 0 || conditionValue.items.length === 0)) {
      conditionResult = question.required
    } else {
      conditionResult = this.questionnaireConditionChecker.check(conditionValue, {
          invisibleQuestions: this.conditionsDTO.invisibleQuestions,
          ...scopedContext
        })?.result || false
    }

    return conditionResult
  }

  createQuestionConditions(scopedContext) {
    if (!this.conditionsDTO.configuration.hasOwnProperty('questions')) {
      return
    }
    this.conditionsDTO.visibleQuestionsPerScreen.clear()
    this.conditionsDTO.visibleQuestionsPerGroup.clear()
    const questions = Object.values(this.conditionsDTO.configuration.questions).sort(
      (a, b) => a.dependencyOrder - b.dependencyOrder
    )
    if (this.conditionsDTO.focused) {
      questions.forEach( function (question) { this.initVisibleQuestionsSets(question) }.bind(this))
    }
    questions.forEach(
      function (question) {
        if (
          this.conditionsDTO.focused &&
          this.conditionsDTO.context?.currentQuestionId?.toString() !== question?.id?.toString()
        ) {
          return
        }
        if (this.conditionsDTO.conditionsSetInAdvanceForQuestionIds.has(question.id.toString())) {
          return
        }
        this.handleQuestionConditions(question, null, scopedContext, false)
      }.bind(this)
    )
  }

  handleScreenConditions(screen, scopedContext) {
    if (!screen) {
      return;
    }
    const screenId = screen.id
    const screenConditions = Object.entries(screen.conditions)
    scopedContext.attachementType = 'none' // there is no attachment for a screen => initialize to calculate conditions
    scopedContext.attachementId = null // there is no attachment for a screen => initialize to calculate conditions
    screenConditions.forEach(
      function ([conditionType, conditionValue]) {
        const conditionResult = this.questionnaireConditionChecker.check(conditionValue, scopedContext)?.result || false
        const currentScreenMap = this.conditionsDTO.screensToInsert.get(screenId.toString()) || new Map()
        currentScreenMap.set(conditionType, conditionResult)
        this.conditionsDTO.screensToInsert.set(screenId.toString(), currentScreenMap)
        this.conditionsDTO.addInvisibleScreenToInvisibleList(conditionResult, conditionType, screenId)
      }.bind(this)
    )
  }

  handleGroupConditions(group, scopedContext) {
    if (!group) {
      return;
    }
    const groupId = group.id
    const groupConditions = Object.entries(group.conditions)
    this.calculateGroupConditionsForScreenWithAttachments(group, scopedContext, groupId, groupConditions)
    this.calculateGroupConditionsForScreenWithoutAttachments(scopedContext, groupConditions, group, groupId)
  }

  calculateGroupConditionsForScreenWithoutAttachments(scopedContext, groupConditions, group, groupId) {
    if (this.conditionsDTO.screenHasAttachments(group.screen_id)) {
      return
    }
    scopedContext.attachementType = 'none'
    scopedContext.attachementId = null
    scopedContext.prod = null
    this.calculateGroupConditions(groupConditions, group, scopedContext, groupId.toString(), groupId)
  }

  calculateGroupConditionsForScreenWithAttachments(group, scopedContext, groupId, groupConditions) {
    if (!this.conditionsDTO.screenHasAttachments(group.screen_id)) {
      return
    }
    const screensAttachments = this.conditionsDTO.screensWithAttachmentsList[group.screen_id]
    screensAttachments.attachmentIds.forEach(function (attachmentId) {
      scopedContext.attachementType = screensAttachments.attachmentType
      scopedContext.attachementId = attachmentId
      scopedContext.prod = this.conditionsDTO.setProdContext(screensAttachments.attachmentType, attachmentId)
      const itemBuiltId = questionsHandler.buildId(groupId, screensAttachments.attachmentType, attachmentId)
      this.calculateGroupConditions(groupConditions, group, scopedContext, itemBuiltId, groupId, screensAttachments.attachmentType, attachmentId)
    }.bind(this))
  }

  calculateGroupConditions(
    groupConditions,
    group,
    scopedContext,
    itemBuiltId,
    groupId,
    attachmentType = 'none',
    attachmentId = null
  ) {
    groupConditions.forEach(
      function ([conditionType, conditionValue]) {
        let conditionResult = false
        if (!(group.screen_id && this.conditionsDTO.groupBelongsToInvisibleScreen(group.screen_id))) {
          conditionResult = this.questionnaireConditionChecker.check(conditionValue, scopedContext)?.result || false
        }
        const currentGroupMap = this.conditionsDTO.groupsToInsert.get(itemBuiltId) || new Map()
        currentGroupMap.set(conditionType, conditionResult)
        this.conditionsDTO.groupsToInsert.set(itemBuiltId, currentGroupMap)
        this.conditionsDTO.addInvisibleGroupToInvisibleList(conditionResult, conditionType, groupId, attachmentType, attachmentId)
      }.bind(this)
    )
  }

  initVisibleQuestionsSets(question) {
    const questionId = question.id?.toString()
    const screenId = question.group?.screen?.id?.toString()
    const groupId = question.group?.id?.toString()
    if (this.conditionsDTO.screenHasAttachments(screenId)) {
      const screensAttachments = this.conditionsDTO.screensWithAttachmentsList[screenId]
      screensAttachments.attachmentIds.forEach(
        function (attachmentId) {
          const questionBuiltId = questionsHandler.buildId(questionId, screensAttachments.attachmentType, attachmentId)
          const groupBuiltId = questionsHandler.buildId(groupId, screensAttachments.attachmentType, attachmentId)
          this.conditionsDTO.addQuestionToVisibleQuestionsPerScreen(screenId, questionBuiltId)
          this.conditionsDTO.addQuestionToVisibleQuestionsPerGroup(groupBuiltId, questionBuiltId)
        }.bind(this)
      )
    } else {
      this.conditionsDTO.addQuestionToVisibleQuestionsPerScreen(screenId, questionId)
      this.conditionsDTO.addQuestionToVisibleQuestionsPerGroup(groupId, questionId)
    }
  }

  handleQuestionConditionsForScreenWithoutAttachments(
    question,
    questionId,
    screenId,
    groupId,
    questionConditions,
    questionIsVisible,
    scopedContext,
    parentQuestionId
  ) {
    if (this.conditionsDTO.screenHasAttachments(screenId)) {
      return
    }
    questionIsVisible = true
    scopedContext.attachementType = 'none'
    scopedContext.attachementId = null
    scopedContext.prod = null
    scopedContext.currentValue = this.conditionsDTO.answerValuePerCode?.[questionId]

    this.calculateConditions(
      questionConditions,
      question,
      scopedContext,
      'none',
      null,
      parentQuestionId,
      questionIsVisible,
      questionId,
      screenId,
      questionId,
      groupId
    )
  }

  handleQuestionConditionsForScreenWithAttachments(
    question,
    questionId,
    screenId,
    groupId,
    isDependency,
    questionConditions,
    questionIsVisible,
    scopedContext,
    parentQuestionId
  ) {
    if (!this.conditionsDTO.screenHasAttachments(screenId)) {
      return
    }
    const screensAttachments = this.conditionsDTO.screensWithAttachmentsList[screenId]
    screensAttachments.attachmentIds.forEach(
      function (attachmentId) {
        if (
          this.conditionsDTO.focused &&
          !isDependency &&
          (this.conditionsDTO.context?.attachementType !== screensAttachments.attachmentType ||
            this.conditionsDTO.context?.attachementId?.toString() !== attachmentId?.toString())
        ) {
          return
        }
        const questionBuiltId = questionsHandler.buildId(questionId, screensAttachments.attachmentType, attachmentId)
        const groupBuiltId = questionsHandler.buildId(groupId, screensAttachments.attachmentType, attachmentId)
        questionIsVisible = true
        scopedContext.attachementType = screensAttachments.attachmentType
        scopedContext.attachementId = attachmentId
        scopedContext.prod = this.conditionsDTO.setProdContext(screensAttachments.attachmentType, attachmentId)
        scopedContext.currentValue = this.conditionsDTO.answerValuePerCode?.[questionBuiltId]
        this.calculateConditions(
          questionConditions,
          question,
          scopedContext,
          screensAttachments,
          attachmentId,
          parentQuestionId,
          questionIsVisible,
          questionId,
          screenId,
          questionBuiltId,
          groupBuiltId
        )
      }.bind(this)
    )
  }

  calculateConditions(
    questionConditions,
    question,
    scopedContext,
    screensAttachments,
    attachmentId,
    parentQuestionId,
    questionIsVisible,
    questionId,
    screenId,
    questionBuiltId,
    groupBuiltId
  ) {
    questionConditions.forEach(
      function ([conditionType, conditionValue]) {
        const conditionResult = this.calculateQuestionConditionResult(
          conditionType,
          conditionValue,
          question,
          scopedContext,
          screensAttachments.attachmentType,
          attachmentId,
          parentQuestionId
        )
        const currentQuestionsMap = this.conditionsDTO.questionsToInsert.get(questionBuiltId) || new Map()
        currentQuestionsMap.set(conditionType, conditionResult)
        this.conditionsDTO.questionsToInsert.set(questionBuiltId, currentQuestionsMap)
        if (conditionType === 'visibility' && !conditionResult) {
          questionIsVisible = false
        }
        this.conditionsDTO.addOrRemoveInvisibleQuestionFromInvisibleList(
          conditionResult,
          conditionType,
          questionId,
          screensAttachments.attachmentType,
          attachmentId
        )
      }.bind(this)
    )
    if (!questionIsVisible) {
      const currentQuestionsMap = this.conditionsDTO.questionsToInsert.get(questionBuiltId) || new Map()
      currentQuestionsMap.set('mandatory', false)
      this.conditionsDTO.questionsToInsert.set(questionBuiltId, currentQuestionsMap)
      this.conditionsDTO.removeQuestionFromVisibleQuestionsPerScreen(screenId, questionBuiltId)
      this.conditionsDTO.removeQuestionFromVisibleQuestionsPerGroup(groupBuiltId, questionBuiltId)
    } else {
      this.conditionsDTO.addQuestionToVisibleQuestionsPerScreen(screenId, questionBuiltId)
      this.conditionsDTO.addQuestionToVisibleQuestionsPerGroup(groupBuiltId, questionBuiltId)
    }
  }

  handleQuestionConditions(
    question,
    parentQuestionId,
    scopedContext,
    isDependency = false
  ) {
    let questionIsVisible = true
    const questionId = question.id?.toString()
    const questionConditions = Object.entries(question.conditions)
    const screenId = question.group?.screen?.id?.toString()
    const groupId = question.group?.id?.toString()
    this.handleQuestionConditionsForScreenWithAttachments(
      question,
      questionId,
      screenId,
      groupId,
      isDependency,
      questionConditions,
      questionIsVisible,
      scopedContext,
      parentQuestionId
    )
    this.handleQuestionConditionsForScreenWithoutAttachments(
      question,
      questionId,
      screenId,
      groupId,
      questionConditions,
      questionIsVisible,
      scopedContext,
      parentQuestionId
    )
    this.updateDirectDependencyScreenCondition(question)
    this.updateDirectDependencyGroupCondition(question)
    this.recursiveCalculateImpactedQuestionsConditions(question, questionIsVisible)
  }

  recursiveCalculateImpactedQuestionsConditions(question, questionIsVisible) {
    const questionId = question.id?.toString()
    Object.keys(question.impacts)?.map(
      function (impactedQuestionId) {
        return this.conditionsDTO.configuration.questions[impactedQuestionId]
      }.bind(this))?.sort(function (a, b) { return a.dependencyOrder - b.dependencyOrder })?.forEach(
      function (impactedQuestion) {
        if ( this.conditionsDTO.conditionsSetInAdvanceForQuestionIds.has(impactedQuestion?.id?.toString())) {
          return
        }
        this.handleQuestionConditions(
          impactedQuestion,
          questionId,
          { ...this.conditionsDTO.context },
          true
        )
        this.conditionsDTO.conditionsSetInAdvanceForQuestionIds.add(impactedQuestion?.id?.toString())
      }.bind(this)
    )
  }

  updateDirectDependencyGroupCondition(question) {
    Object.keys(question.directImpacts['group'])?.map(
      function (impactedGroupId) {
        return this.conditionsDTO.associativeGroup?.[impactedGroupId.toString()]
      }.bind(this)
    )?.forEach(
      function (impactedGroup) {
        this.handleGroupConditions(impactedGroup, { ...this.conditionsDTO.context })
      }.bind(this)
    )
  }

  updateDirectDependencyScreenCondition(question) {
    Object.keys(question.directImpacts['screen'])
      ?.map(
        function (impactedScreenId) {
          return this.conditionsDTO.associativeScreen?.[impactedScreenId.toString()]
        }.bind(this)
      )
      ?.forEach(
        function (impactedScreen) {
          this.handleScreenConditions(impactedScreen, { ...this.conditionsDTO.context })
        }.bind(this)
      )
  }
}